import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  PersonAdd,
  People,
  Visibility,
  Search,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetUsersQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteUserMutation } from '../store/api/usersApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { hasPermission } from '../utils/permissions'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx } from '../theme/sectionSurfaces'

const ROLES = [
  { value: 'secretary', label: 'Ban Thư ký' },
  { value: 'judge', label: 'Giám khảo' },
  { value: 'student', label: 'Sinh viên' },
  { value: 'support', label: 'Hỗ trợ kỹ thuật' },
]

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  secretary: 'error',
  judge: 'primary',
  student: 'success',
  support: 'warning',
}

const STATUS_FILTERS = [
  { value: 'all', label: 'Tất cả trạng thái' },
  { value: 'active', label: 'Hoạt động' },
  { value: 'pending', label: 'TK tạm' },
  { value: 'inactive', label: 'Vô hiệu hóa' },
]

export default function UsersPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: users = [], isLoading, error, refetch } = useGetUsersQuery()
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation()
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [editingUser, setEditingUser] = useState<any>(null)
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    role: 'student',
    password: '',
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  // C — filter user theo đội. Value = team_id; UI hiển thị team.name.
  const [teamFilter, setTeamFilter] = useState('all')
  const { data: teamsList = [] } = useGetTeamsQuery()
  // Map user_id → Set<team_id> để filter O(1) thay vì O(n*m)
  const userTeamsMap = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const t of teamsList) {
      if (t.status === 'deleted') continue
      for (const uid of t.members || []) {
        if (!uid) continue
        if (!m.has(uid)) m.set(uid, new Set())
        m.get(uid)!.add(t.id)
      }
    }
    return m
  }, [teamsList])
  // Active teams cho dropdown — sort theo tên
  const activeTeams = useMemo(
    () => teamsList.filter((t) => t.status !== 'deleted').sort((a, b) => a.name.localeCompare(b.name, 'vi')),
    [teamsList],
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Check permission
  const canManageUsers = currentUser && hasPermission(currentUser.role, 'MANAGE_USERS')

  const filteredUsers = users.filter((user: any) => {
    const query = searchTerm.trim().toLowerCase()
    // C — search mở rộng: tên user / email / full_name / role / tên đội user thuộc
    const userTeamIds = userTeamsMap.get(user.id) || new Set<string>()
    const userTeamNames = teamsList
      .filter((t) => userTeamIds.has(t.id))
      .map((t) => t.name)
    const matchesSearch =
      !query ||
      [user.username, user.email, user.full_name, user.role, ...userTeamNames]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    const matchesRole = roleFilter === 'all' || user.role === roleFilter
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'pending' && user.is_placeholder) ||
      (statusFilter === 'active' && user.is_active && !user.is_placeholder) ||
      (statusFilter === 'inactive' && !user.is_active && !user.is_placeholder)
    // C — match theo đội: 'all' bỏ qua; '__none__' = user không thuộc đội nào; còn lại = team_id cụ thể.
    const matchesTeam =
      teamFilter === 'all' ||
      (teamFilter === '__none__' && userTeamIds.size === 0) ||
      userTeamIds.has(teamFilter)

    return matchesSearch && matchesRole && matchesStatus && matchesTeam
  })

  const activeCount = users.filter((user: any) => user.is_active && !user.is_placeholder).length
  const pendingCount = users.filter((user: any) => user.is_placeholder).length
  const inactiveCount = users.filter((user: any) => !user.is_active && !user.is_placeholder).length

  useEffect(() => {
    if (!canManageUsers) {
      setErrorMessage('Bạn không có quyền truy cập trang này. Chỉ Secretary và Support mới có thể quản lý người dùng.')
    }
  }, [canManageUsers])

  // View user dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingUser, setViewingUser] = useState<any>(null)

  // Create user function - matches moduleFunctions.ts
  const handleCreate = () => {
    setEditingUser(null)
    setFormData({
      username: '',
      email: '',
      full_name: '',
      role: 'student',
      password: '',
    })
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  // Update user function - matches moduleFunctions.ts
  const handleUpdate = (user: any) => {
    setEditingUser(user)
    setFormData({
      username: user.username || '',
      email: user.email || '',
      full_name: user.full_name || '',
      role: user.role || 'student',
      password: '', // Don't prefill password
    })
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  // View user function - matches moduleFunctions.ts
  const handleView = (user: any) => {
    setViewingUser(user)
    setViewDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingUser(null)
    setFormData({
      username: '',
      email: '',
      full_name: '',
      role: 'student',
      password: '',
    })
    // Chỉ clear error message trong dialog, không clear success message
    // Success message sẽ được hiển thị trên trang chính
    setErrorMessage(null)
  }

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingUser(null)
  }

  // Submit handler - internal function, calls handleCreate or handleUpdate logic
  const handleSubmit = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      if (editingUser) {
        // Update user
        const updateData: any = {
          username: formData.username,
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
        }
        if (formData.password) {
          updateData.password = formData.password
        }
        await updateUser({ id: editingUser.id, ...updateData } as any).unwrap()
        // Đóng dialog trước, sau đó set success message để hiển thị trên trang chính
        handleCloseDialog()
        setSuccessMessage('Cập nhật người dùng thành công!')
      } else {
        // Create user
        if (!formData.password) {
          setErrorMessage('Mật khẩu là bắt buộc khi tạo người dùng mới')
          return
        }
        await createUser(formData).unwrap()
        // Đóng dialog trước, sau đó set success message để hiển thị trên trang chính
        handleCloseDialog()
        setSuccessMessage('Tạo người dùng thành công!')
      }
      // Refresh danh sách users
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa người dùng này?')) {
      return
    }

    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      await deleteUser(userId).unwrap()
      setSuccessMessage('Xóa người dùng thành công!')
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi xóa người dùng')
    }
  }

  if (!canManageUsers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Bạn không có quyền truy cập trang này. Chỉ Secretary và Support mới có thể quản lý người dùng.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Quản lý Người dùng"
        subtitle="Tạo và cập nhật tài khoản theo vai trò (Ban Thư ký, Giám khảo, Sinh viên, …)."
        icon={<People />}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAdd />}
            onClick={handleCreate}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
            }}
          >
            Tạo người dùng mới
          </Button>
        }
      />

      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }} 
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={(theme) => ({
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) 200px 200px 220px' },
        })}
      >
        <TextField
          label="Tìm kiếm người dùng"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Vai trò"
          select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          size="small"
          fullWidth
        >
          <MenuItem value="all">Tất cả vai trò</MenuItem>
          {ROLES.map((role) => (
            <MenuItem key={role.value} value={role.value}>
              {role.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Trạng thái"
          select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          size="small"
          fullWidth
        >
          {STATUS_FILTERS.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
        </TextField>
        {/* C — filter theo đội (UI hiển thị team name; value lưu team_id) */}
        <TextField
          label="Đội thi"
          select
          value={teamFilter}
          onChange={(event) => setTeamFilter(event.target.value)}
          size="small"
          fullWidth
        >
          <MenuItem value="all">Tất cả đội</MenuItem>
          <MenuItem value="__none__">— Chưa thuộc đội —</MenuItem>
          {activeTeams.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Username</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Họ tên</strong></TableCell>
                <TableCell><strong>Vai trò</strong></TableCell>
                <TableCell><strong>Trạng thái</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {users.length === 0 ? 'Chưa có người dùng nào' : 'Không có người dùng khớp bộ lọc'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user: any) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={ROLES.find(r => r.value === user.role)?.label || user.role}
                        color={ROLE_COLORS[user.role] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.is_placeholder ? (
                          <Chip label="TK tạm" color="warning" size="small" variant="outlined" />
                        ) : (
                          <Chip
                            label={user.is_active ? 'Hoạt động' : 'Vô hiệu hóa'}
                            color={user.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleView(user)}
                        color="info"
                        title="Xem chi tiết"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdate(user)}
                        color="primary"
                        title="Chỉnh sửa"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user.id)}
                        color="error"
                        disabled={user.id === currentUser?.id} // Can't delete yourself
                        title="Xóa"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Hiển thị {filteredUsers.length}/{users.length} người dùng
                    </Typography>
                    <Chip label={`Hoạt động: ${activeCount}`} color="success" size="small" variant="outlined" />
                    <Chip label={`TK tạm: ${pendingCount}`} color="warning" size="small" variant="outlined" />
                    <Chip label={`Vô hiệu hóa: ${inactiveCount}`} size="small" variant="outlined" />
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Chỉnh sửa người dùng' : 'Tạo người dùng mới'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {editingUser?.is_placeholder && (
              <Alert severity="warning">
                Đây là tài khoản tạm được tạo từ đăng ký đội. Tài khoản chưa đăng nhập được cho đến khi được cấp mật khẩu và kích hoạt.
              </Alert>
            )}
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Họ tên"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Vai trò"
              select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              fullWidth
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={editingUser ? 'Mật khẩu mới (để trống nếu không đổi)' : 'Mật khẩu'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingUser}
              fullWidth
              helperText={editingUser?.is_placeholder ? 'Điền mật khẩu để kích hoạt tài khoản tạm.' : editingUser ? 'Chỉ điền nếu muốn đổi mật khẩu' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Hủy</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
            }}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : (editingUser ? 'Cập nhật' : 'Tạo')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Chi tiết người dùng</DialogTitle>
        <DialogContent>
          {viewingUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Username"
                value={viewingUser.username}
                disabled
                fullWidth
              />
              <TextField
                label="Email"
                value={viewingUser.email}
                disabled
                fullWidth
              />
              <TextField
                label="Họ tên"
                value={viewingUser.full_name || '-'}
                disabled
                fullWidth
              />
              <TextField
                label="Vai trò"
                value={ROLES.find(r => r.value === viewingUser.role)?.label || viewingUser.role}
                disabled
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {viewingUser.is_placeholder ? (
                  <Chip label="TK tạm" color="warning" size="small" variant="outlined" />
                ) : (
                  <Chip
                    label={viewingUser.is_active ? 'Hoạt động' : 'Vô hiệu hóa'}
                    color={viewingUser.is_active ? 'success' : 'default'}
                    size="small"
                  />
                )}
                <Chip
                  label={ROLES.find(r => r.value === viewingUser.role)?.label || viewingUser.role}
                  color={ROLE_COLORS[viewingUser.role] || 'default'}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
          {viewingUser && (
            <Button
              onClick={() => {
                handleCloseViewDialog()
                handleUpdate(viewingUser)
              }}
              variant="contained"
              startIcon={<Edit />}
            >
              Chỉnh sửa
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}
